import cx from 'classnames';
import { MinusCircle, UploadCloud01 } from 'ethena-icons';
import _ from 'lodash';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Spinner } from 'react-bootstrap';
import { FileIcon } from '../FileIcon';
import css from './MultiFileUpload.module.scss';

interface IMultiFileUploadProps {
  accept: string;
  className?: string;
  filesWithErrors: { fileName: string; message?: string }[];
  isDisabled?: boolean;
  label?: string;
  maxFileSizeMB: number;
  onChange: (files: File[]) => void;
  onRemove: (name: string) => void;
  setFilesWithError: Dispatch<
    SetStateAction<
      {
        fileName: string;
        message?: string;
      }[]
    >
  >;
  uploadPercentages: { [key: string]: number };
}

export const MultiFileUpload = ({
  accept,
  className,
  filesWithErrors,
  isDisabled = false,
  label = 'Select files...',
  maxFileSizeMB,
  onChange,
  onRemove,
  setFilesWithError,
  uploadPercentages,
}: IMultiFileUploadProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const [files, setFiles] = useState<File[]>([]);
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    return () => {
      _.forEach(fileUrls, (url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = _.toArray(ev.target.files);
    const filesWithErrors = [];
    const filesWithoutErrors = [];

    const newUrls: { [key: string]: string } = {};
    for (let file of selectedFiles) {
      if (maxFileSizeMB && file.size > maxFileSizeMB * 1024 * 1024) {
        filesWithErrors.push({
          fileName: file.name,
          message: `File size must be less than ${maxFileSizeMB}MB`,
        });
      } else {
        filesWithoutErrors.push(file);
      }

      newUrls[file.name] = URL.createObjectURL(file);
    }

    setFilesWithError((prevFilesWithErrors) => [
      ...prevFilesWithErrors,
      ...filesWithErrors,
    ]);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    setFileUrls((prevUrls) => ({ ...prevUrls, ...newUrls }));

    if (!_.isEmpty(filesWithoutErrors)) {
      onChange(filesWithoutErrors);
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemoveFile = (name: string) => {
    if (fileUrls[name]) {
      URL.revokeObjectURL(fileUrls[name]);
      const updatedFileUrls = _.omit(fileUrls, name);
      setFileUrls(updatedFileUrls);
    }

    const updatedFiles = _.reject(files, (file) => file.name === name);
    setFiles(updatedFiles);
    onRemove(name);
  };

  const renderFiles = () => {
    return _.map(files, (file) => {
      const isUploading = uploadPercentages[file.name] < 100;
      const error = _.find(filesWithErrors, (x) => x.fileName === file.name);
      return (
        <div key={file.name}>
          <div className={css.fileRow}>
            <div
              className={cx(css.fileContainer, {
                [css.fileContainerError]: !!error,
              })}
            >
              <a
                href={fileUrls[file.name]}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileIcon fileName={file.name} className={css.file} />
                {file.name}
              </a>
            </div>
            {isUploading && (
              <Spinner
                animation="border"
                className={css.spinner}
                role="status"
              />
            )}
            {!isDisabled && !isUploading && (
              <MinusCircle
                className={css.remove}
                height={16}
                width={16}
                onClick={() => handleRemoveFile(file.name)}
              />
            )}
          </div>
          {!!error && (
            <p className={css.errorMessage}>
              {error.message || 'Upload failed, please try again'}
            </p>
          )}
        </div>
      );
    });
  };

  return (
    <div className={cx(css.container, className)}>
      <button
        className={css.pseudoInput}
        onClick={() => inputRef.current?.click?.()}
        type="button"
      >
        {label}
        <UploadCloud01 height={16} width={16} />
      </button>
      <input
        accept={accept}
        hidden
        multiple
        onChange={handleChange}
        ref={inputRef}
        type="file"
      />
      {renderFiles()}
    </div>
  );
};
